import type { SelectChangeEvent } from "@mui/material";
import {
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DwhListingType } from "../../../api/types/integrationsTypes";
import type { AddShareForm, AddShareFormFields } from "./addShareForm";
import {
  initialAddShareForm,
  isAddShareFormValid,
  mapAddShareFormToRequest,
  validateAddShareForm,
} from "./addShareForm";

interface Props {
  onCancel: () => void;
  onShareCreated: (accountCode: string) => void;
}

const createDwhShare = withErrorHandling(adminApi.postDwhShare);

const AddShareDialog = ({ onCancel, onShareCreated }: Props) => {
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<AddShareForm>(initialAddShareForm);

  const handleEdit =
    (field: AddShareFormFields): React.ChangeEventHandler<HTMLInputElement> =>
    ({ target }) =>
      setForm((currentForm) =>
        validateAddShareForm({
          ...currentForm,
          [field]: target.value,
        })
      );

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setForm((currentForm) => ({
      ...currentForm,
      type: event.target.value as DwhListingType,
    }));
  };

  const handleCancel = () => {
    onCancel();
    setSaving(false);
  };

  const handleSave = async () => {
    const validatedForm = validateAddShareForm(form);
    if (!isAddShareFormValid(validatedForm)) {
      setForm(validatedForm);
      return;
    }

    setSaving(true);
    const [, error] = await createDwhShare(mapAddShareFormToRequest(validatedForm));
    setSaving(false);

    if (error) {
      logError(error, "[AddShareDialog] handleSave");
      setForm({ ...form, errors: { accountCode: error.message || "Server error" } });
      return;
    }

    onShareCreated(form.accountCode.toUpperCase());
  };

  return (
    <SmallDialog open onClose={handleCancel}>
      <DialogTitle>Share Data</DialogTitle>
      <DialogCloseButton onClick={handleCancel} />
      <Divider />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Type
        </Typography>
        <Select fullWidth variant="outlined" value={form.type} onChange={handleTypeChange}>
          <MenuItem key="AllTables" value="AllTables">
            All Tables
          </MenuItem>
          <MenuItem key="Qashqade" value="Qashqade">
            Qashqade
          </MenuItem>
          <MenuItem key="Cascata" value="Cascata">
            Cascata
          </MenuItem>
          <MenuItem key="PortfolioMonitoring" value="PortfolioMonitoring">
            Portfolio Monitoring
          </MenuItem>
        </Select>
        <Typography color="textSecondary" sx={{ mb: 1, mt: 3 }}>
          Paste the Snowflake account code for data sharing in the field below.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Account Code
        </Typography>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder="XXXXXXX.XXXXXXX"
          value={form.accountCode}
          onChange={handleEdit("accountCode")}
          error={!!form.errors.accountCode}
          helperText={form.errors.accountCode}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 1 }}>
          <Typography variant="subtitle2">Description</Typography>
          <Typography color="textSecondary">(optional)</Typography>
        </Stack>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Add text..."
          value={form.description}
          onChange={handleEdit("description")}
          spellCheck
        />
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={handleCancel}>
        <Button
          variant="contained"
          color="primary"
          loading={saving}
          onClick={handleSave}
          disabled={!isAddShareFormValid(form)}
        >
          Share
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddShareDialog;
